// The navigator: ask the model for the next decision and validate its shape.

import { complete } from "../api/model";
import { PRI, type Priority } from "../api/scheduler";
import { parseModelJSON } from "../lib/json-repair";
import { clamp, tidyProse } from "../lib/text";
import { navPrompt } from "./prompts";
import { traceParseOutcome } from "./telemetry";
import { groundingLine, newNode, pathTo, state, type DecisionNode, type Stakes } from "./state";

export interface ProposedDecision {
  branch: string;
  question: string;
  options: { label: string; tradeoff: string }[];
  recommend: number;
  stakes: Stakes;
  confidence: number;
  latency: number;
}

// Sampling knob for the navigator call — the eval harness overrides this to
// A/B how temperature affects JSON integrity vs option diversity.
export const navTuning = { temp: 0.6 };

export interface ProposeArgs {
  parentNode?: DecisionNode | null;
  optionIndex?: number | null;
  isRoot?: boolean;
  signal?: AbortSignal;
  priority?: Priority;
}

function breadcrumbForChild(parentNode: DecisionNode, optionIndex: number | null): string {
  const chain = pathTo(parentNode);
  const lines: string[] = [];
  for (const nd of chain) {
    const chosen = nd === parentNode ? optionIndex : nd.chosenIndex;
    if (chosen == null) continue;
    lines.push(`- [${nd.branch || "root"}] ${nd.question} → CHOSE: ${nd.options[chosen]?.label}`);
  }
  return lines.join("\n") || "(none yet)";
}

/**
 * The pure model step — ask the navigator for the next decision and return
 * validated data. No graph mutation, so callers that only want to *refresh*
 * an existing node (injectFact) can reuse it too.
 */
export async function proposeDecision({ parentNode, optionIndex, isRoot = false, signal, priority = PRI.FG }: ProposeArgs): Promise<ProposedDecision> {
  const pathText = isRoot ? "(none yet)" : breadcrumbForChild(parentNode!, optionIndex ?? null);
  const prompt = navPrompt(
    { idea: state.idea, grounding: groundingLine(), facts: state.facts, constraints: state.constraints },
    { pathText, isRoot },
  );
  const t0 = performance.now();
  let data: any;
  for (let attempt = 0; attempt < 2; attempt++) {
    let raw: string | null = null;
    try {
      raw = await complete(prompt, { max: 360, temp: navTuning.temp, signal, priority, kind: "nav.propose" });
    } catch (e) {
      if (attempt === 1) throw e;   // HTTP failure — parse never happened
      continue;
    }
    const { data: parsed, parse } = parseModelJSON(raw);
    traceParseOutcome("nav.propose", parse, raw);
    if (parsed && Array.isArray(parsed.options) && parsed.options.length) { data = parsed; break; }
    if (attempt === 1) throw new Error("navigator json unusable");
  }
  const options = (data.options || []).slice(0, 4).map((o: any) => ({
    label: tidyProse(o.label) || "option",
    tradeoff: tidyProse(o.tradeoff),
  }));
  const recommend = Number.isInteger(data.recommend) && data.recommend >= 0 && data.recommend < options.length
    ? data.recommend : 0;
  return {
    branch: (data.branch || parentNode?.branch || "design").trim(),
    question: tidyProse(data.question) || "What next?",
    options,
    recommend,
    stakes: ["low", "med", "high"].includes(data.stakes) ? data.stakes : "med",
    confidence: data.confidence ?? 0.4,
    latency: Math.round(performance.now() - t0),
  };
}

/** Overwrite an existing node's decision fields in place (identity/links kept). */
export function applyDecision(node: DecisionNode, data: ProposedDecision): void {
  node.branch = data.branch;
  node.question = data.question;
  node.options = data.options;
  node.recommend = data.recommend;
  node.stakes = data.stakes;
  node.confidence = clamp(data.confidence, 0.1, 0.9);
  node.lastLatency = data.latency;
}

/** Propose a decision + wrap it in a fresh detached node. */
export async function generateNode(args: ProposeArgs): Promise<DecisionNode> {
  const data = await proposeDecision(args);
  return newNode({
    parentId: args.parentNode ? args.parentNode.id : null,
    depth: args.parentNode ? args.parentNode.depth + 1 : 0,
    branch: data.branch,
    question: data.question,
    options: data.options,
    recommend: data.recommend,
    stakes: data.stakes,
    confidence: clamp(data.confidence, 0.1, 0.95),
    lastLatency: data.latency,
  });
}
